import { z } from "zod/v4";

import {
  AuditBriefing,
  BriefingBrandSchema,
  BriefingFunnelSchema,
  BriefingStoreFactsSchema,
  BriefingSummarySchema,
} from "./present-audit-briefing";
import { ReadResult, readVersioned, Upcasters } from "./versioned-payload";

type BriefingRegionKey = Exclude<keyof AuditBriefing, "readAt">;

interface AnalysisTypeEntry {
  version: number;
  schema: z.ZodType;
  upcasters: Upcasters;
}

/**
 * Each analysis type's schema and version. Every briefing region is a type, so
 * a region with no entry fails the build. A type outside the briefing is one
 * more entry.
 */
export const AUDIT_ANALYSES = {
  storeFacts: { version: 1, schema: BriefingStoreFactsSchema, upcasters: {} },
  brand: { version: 1, schema: BriefingBrandSchema, upcasters: {} },
  funnel: {
    version: 2,
    schema: BriefingFunnelSchema,
    upcasters: {
      // Every funnel before version 2 covered 90 days: the briefing asked for
      // 90 from before the funnel existed.
      1: (raw) => ({ windowDays: 90, ...(raw as object) }),
    },
  },
  summary: {
    version: 2,
    schema: BriefingSummarySchema,
    upcasters: { 1: (raw) => ({ ...(raw as object), writtenFrom: null }) },
  },
} satisfies Record<BriefingRegionKey, AnalysisTypeEntry> &
  Record<string, AnalysisTypeEntry>;

export type AuditAnalysisType = keyof typeof AUDIT_ANALYSES;

export const isAuditAnalysisType = (type: string): type is AuditAnalysisType =>
  type in AUDIT_ANALYSES;

export function readAnalysis(
  type: string,
  schemaVersion: number | undefined,
  body: unknown
): ReadResult<unknown> {
  if (!isAuditAnalysisType(type)) {
    return {
      type: "unreadable",
      version: schemaVersion ?? 0,
      reason: `Unknown analysis type: ${type}`,
    };
  }
  const entry: AnalysisTypeEntry = AUDIT_ANALYSES[type];
  return readVersioned({
    raw: body,
    version: schemaVersion,
    current: entry.version,
    upcasters: entry.upcasters,
    schema: entry.schema,
  });
}

const REGIONS = ["storeFacts", "brand", "funnel", "summary"] as const;

/**
 * A stored briefing, one region at a time. A region an older briefing never
 * had stays absent. A region that does not read comes back as failed, with a
 * reason the panel can show.
 */
export function readBriefing(raw: unknown): Partial<AuditBriefing> {
  const stored = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;
  const out: Partial<AuditBriefing> = {};
  if (typeof stored.readAt === "string") {
    out.readAt = stored.readAt;
  }
  for (const key of REGIONS) {
    const region = stored[key] as
      | {
          status?: unknown;
          value?: unknown;
          reason?: unknown;
          recordId?: unknown;
          schemaVersion?: unknown;
        }
      | undefined;
    if (!region || typeof region !== "object") {
      continue;
    }
    const kept = {
      recordId:
        typeof region.recordId === "string" ? region.recordId : undefined,
      schemaVersion:
        typeof region.schemaVersion === "number"
          ? region.schemaVersion
          : undefined,
    };
    if (region.status !== "ok") {
      out[key] = {
        status: region.status === "unavailable" ? "unavailable" : "failed",
        value: null,
        reason: typeof region.reason === "string" ? region.reason : null,
        ...kept,
      } as never;
      continue;
    }
    const read = readAnalysis(key, kept.schemaVersion, region.value);
    out[key] = (
      read.type === "read"
        ? {
            status: "ok",
            value: read.value,
            reason: null,
            ...kept,
            schemaVersion: AUDIT_ANALYSES[key].version,
          }
        : {
            status: "failed",
            value: null,
            reason:
              read.type === "newer"
                ? "A newer version of the audit showed this. Reload to see it."
                : "An earlier version of the audit showed this, and it can no longer be shown.",
            ...kept,
          }
    ) as never;
  }
  return out;
}
